import * as queryString from 'query-string';

/**
 * Google Analytics 4 (GA4) bootstrap.
 *
 * We support one GA4 property ("tag") per skin:
 *   - GHX Fields  -> REACT_APP_GA4_ID_GHX
 *   - Enogen / Eluminate -> REACT_APP_GA4_ID_ENOGEN
 *
 * The skin is detected from the URL at boot, mirroring the logic in
 * src/logic/Utility/useUrlTheme.tsx so analytics and the visual theme agree:
 * we treat the session as Enogen when the origin is eluminate.solutions OR the
 * url carries ?loginstyle=enogen. Everything else is GHX.
 *
 * Marketing gives us a gtag.js snippet with a hardcoded Measurement ID, but we
 * can't paste that into index.html because the skin is only known at runtime.
 * Instead we inject gtag.js programmatically and configure the matching ID.
 */

export enum AnalyticsSkin { GHX = 'ghx', Enogen = 'enogen' }

const GHX_MEASUREMENT_ID = process.env.REACT_APP_GA4_ID_GHX;
const ENOGEN_MEASUREMENT_ID = process.env.REACT_APP_GA4_ID_ELUMINATE;

/**
 * Decide which skin this session belongs to, from the URL only (boot time).
 * Kept in sync with useUrlTheme.tsx.
 */
export function detectSkinFromUrl(): AnalyticsSkin
{
	const parsed = queryString.parse(location.search);
	const loginStyle = typeof parsed.loginstyle === 'string' ? parsed.loginstyle.toLowerCase() : '';

	if (location.origin.includes('eluminate.solutions') || loginStyle === 'enogen')
	{
		return AnalyticsSkin.Enogen;
	}

	return AnalyticsSkin.GHX;
}

function measurementIdForSkin(skin: AnalyticsSkin): string | undefined
{
	return skin === AnalyticsSkin.Enogen ? ENOGEN_MEASUREMENT_ID : GHX_MEASUREMENT_ID;
}

/**
 * GA4 must only run in production. We never want to track qa/uat/dev/local
 * traffic. This is enforced two ways:
 *   1. The GA4 Measurement IDs are only present in .env.production.
 *   2. This hostname guard blocks any non-production host even if an id leaks
 *      into another build.
 * Non-production hosts are localhost and anything carrying a dev/qa/uat segment
 * (e.g. *.qa.fields.ghxseed.com); production is fields.ghxseed.com /
 * eluminate.solutions.
 */
function isProductionHost(): boolean
{
	const host = location.hostname.toLowerCase();

	if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local'))
	{
		return false;
	}

	// Block non-prod environment subdomains.
	return !/(^|\.)(dev|qa|uat)\./.test(host);
}

/**
 * Inject gtag.js and configure GA4 for the current skin. Safe to call once at
 * boot; it no-ops when the matching Measurement ID isn't configured (e.g. local
 * dev or an env without the env var set).
 */
export function initGoogleAnalytics(): void
{
	// Production-only: never track qa/uat/dev/local traffic.
	if (!isProductionHost())
	{
		return;
	}

	const skin = detectSkinFromUrl();
	const measurementId = measurementIdForSkin(skin);

	if (!measurementId)
	{
		// No GA4 id configured for this skin/environment - skip silently.
		return;
	}

	// Standard gtag bootstrap (the snippet marketing provides), but with the id
	// chosen at runtime.
	window.dataLayer = window.dataLayer || [];
	function gtag(...args: unknown[])
	{
		window.dataLayer.push(args);
	}
	window.gtag = gtag;

	gtag('js', new Date());
	gtag('config', measurementId, {
		// Tag the skin so reports can be segmented even within a single property.
		skin
	});

	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
	document.head.appendChild(script);
}
